/**
 * One report template: what it shows, its versions, and a
 * sample-data PDF of the version being looked at. Viewing only; reports are
 * built and changed in chat.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Banner, Button, Skeleton, Table } from '@servicecompressionllc/sc-design-system';
import { Download } from '@servicecompressionllc/sc-design-system/icons';
import { AppShell } from '../../components/AppShell.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { useMe } from '../../hooks/useMe.js';
import { formatDate, formatDateTime } from '../../lib/format.js';
import { canWriteReports } from '../../lib/permissions.js';
import { ReportActions } from './ReportActions.js';
import { ReportSchedulesTab } from './ReportSchedulesTab.js';
import { coversLine, runUnitsLabel } from './reportNarrowing.js';
import { RUN_STATUS, ownerLine, periodLabel, sectionDetail } from './reportDescriptions.js';
import { downloadRunPdf, useReportTemplatePageLogic } from './useReportTemplatePageLogic.js';

const CARD = 'rounded-lg border border-outline bg-surface-elevated p-5';

export const ReportTemplatePage = () => {
  const { id = '' } = useParams<{ id: string }>();
  const me = useMe();
  const meId = me.data?.user.id ?? '';
  const tenantName = me.data?.tenant.name ?? 'your organization';
  const logic = useReportTemplatePageLogic(id, {
    canSchedule: me.data ? canWriteReports(me.data.user) : false,
  });
  const t = logic.template.data;
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const download = async (runId: string) => {
    setDownloadError(null);
    try {
      await downloadRunPdf(runId);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'The PDF could not be downloaded.');
    }
  };

  if (logic.template.isLoading) {
    return (
      <AppShell>
        <Skeleton className="mb-2 h-4 w-40" />
        <Skeleton className="mb-6 h-8 w-96" />
        <Skeleton className="h-96 w-full" />
      </AppShell>
    );
  }

  if (!t) {
    return (
      <AppShell>
        <Breadcrumbs trail={[{ label: 'Reports', to: '/reports' }]} />
        <Banner variant="danger">
          <Banner.Body>
            <Banner.Title>This report isn't available</Banner.Title>
            <Banner.Description>
              {logic.template.error?.message ?? 'It may have been archived or unshared.'}
            </Banner.Description>
          </Banner.Body>
        </Banner>
      </AppShell>
    );
  }

  const access =
    t.visibility === 'tenant'
      ? { label: 'Published', variant: 'success' as const }
      : t.owner.id === meId
        ? { label: 'Private', variant: 'neutral' as const }
        : { label: 'Shared with you', variant: 'info' as const };
  const definition = logic.definition;

  return (
    <AppShell fill>
      <Breadcrumbs trail={[{ label: 'Reports', to: '/reports' }]} />
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="text-2xl font-bold text-ink">{t.name}</h2>
            <Badge variant={access.variant}>{access.label}</Badge>
            <Badge variant="neutral">Version {t.version}</Badge>
          </div>
          <p className="mt-1 text-sm text-ink-muted">{ownerLine(t, meId, tenantName)}</p>
          <p className="mt-0.5 text-sm text-ink-muted">{coversLine(t.template.units)}</p>
        </div>
        <ReportActions key={t.id} template={t} tenantName={tenantName} />
      </div>

      <div className="mb-4 flex gap-1 border-b border-outline" role="tablist" aria-label="Report">
        {logic.tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={logic.tab === tab.id}
            onClick={() => logic.setTab(tab.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              logic.tab === tab.id
                ? 'border-primary text-ink'
                : 'border-transparent text-ink-muted hover:text-ink'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {logic.tab === 'overview' && (
        <div className="grid items-start gap-5 lg:min-h-0 lg:flex-1 lg:grid-cols-[360px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:items-stretch">
          <div className="flex flex-col gap-4 lg:min-h-0 lg:overflow-y-auto">
            {!logic.isCurrent && (
              <Banner variant="info">
                <Banner.Body>
                  <Banner.Title>Showing version {logic.selected}</Banner.Title>
                  <Banner.Description>
                    The report is on version {t.version}.{' '}
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => logic.selectVersion(t.version)}
                    >
                      See the current version
                    </button>
                  </Banner.Description>
                  {t.access.version && logic.selected !== undefined && (
                    <div className="mt-3 flex flex-col gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="self-start"
                        disabled={logic.restore.isPending}
                        onClick={() => {
                          if (logic.selected !== undefined) logic.restore.mutate(logic.selected);
                        }}
                      >
                        {logic.restore.isPending ? 'Restoring…' : 'Restore this version'}
                      </Button>
                      <p className="text-xs text-ink-muted">
                        Saves this layout as version {t.version + 1}. Nothing is deleted.
                      </p>
                      {logic.restore.error && (
                        <p className="text-xs text-danger">{logic.restore.error.message}</p>
                      )}
                    </div>
                  )}
                </Banner.Body>
              </Banner>
            )}

            <section className={CARD} aria-label="What this report shows">
              <h3 className="mb-3 text-[17px] font-semibold text-ink">What this report shows</h3>
              {logic.definitionLoading || !definition ? (
                <Skeleton className="h-32 w-full" />
              ) : (
                <ol className="flex flex-col gap-3">
                  {definition.sections.map((section, i) => (
                    <li key={`${String(i)}-${section.title}`} className="flex items-baseline gap-3">
                      <span className="w-5 flex-none text-xs font-semibold tabular-nums text-ink-subtle">
                        {i + 1}
                      </span>
                      <span className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium text-ink">{section.title}</span>
                        <span className="text-xs text-ink-subtle">{sectionDetail(section)}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <section className={CARD} aria-label="Versions">
              <h3 className="mb-2 text-[17px] font-semibold text-ink">Versions</h3>
              <ul className="flex flex-col">
                {t.versions.map((v) => (
                  <li key={v.number} className="border-t border-outline">
                    <button
                      type="button"
                      aria-current={v.number === logic.selected ? 'true' : undefined}
                      onClick={() => logic.selectVersion(v.number)}
                      className={`flex w-full items-center justify-between gap-3 py-2 text-left text-sm ${
                        v.number === logic.selected ? 'text-ink' : 'text-ink-muted hover:text-ink'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="font-medium">Version {v.number}</span>
                        {v.number === t.version && <Badge variant="neutral">Current</Badge>}
                        {v.restoredFrom != null && (
                          <span className="text-xs text-ink-subtle">
                            restored from version {v.restoredFrom}
                          </span>
                        )}
                      </span>
                      <span className="text-ink-muted">
                        {formatDate(v.createdAt)}
                        {v.createdById === meId ? ', by you' : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-ink-subtle">
                Each change saves a new version, and so does restoring an old one. Open one to see
                what it showed.
              </p>
            </section>
          </div>

          <section
            aria-label="Report preview"
            className="flex min-h-[75vh] flex-col overflow-hidden rounded-lg border border-outline bg-surface-elevated lg:min-h-0"
          >
            <div className="border-b border-outline px-4 py-2.5 text-xs text-ink-muted">
              Preview of version {logic.selected} with sample data. Every name and number is made
              up.
            </div>
            {logic.preview.isLoading && <Skeleton className="m-5 flex-1" />}
            {logic.preview.error && (
              <p className="p-5 text-sm text-ink-muted">{logic.preview.error.message}</p>
            )}
            {logic.preview.data && (
              <iframe
                title="Report preview"
                src={logic.preview.data}
                className="min-h-0 w-full flex-1 bg-white"
              />
            )}
          </section>
        </div>
      )}

      {logic.tab === 'schedules' && <ReportSchedulesTab templateId={t.id} />}

      {logic.tab === 'runs' && (
        <section aria-label="Your runs" className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
          {downloadError && (
            <Banner variant="danger" className="mb-4">
              <Banner.Body>
                <Banner.Title>{downloadError}</Banner.Title>
              </Banner.Body>
            </Banner>
          )}
          {logic.runs.isLoading && <Skeleton className="h-40 w-full" />}
          {logic.runs.error && (
            <Banner variant="danger">
              <Banner.Body>
                <Banner.Title>{logic.runs.error.message}</Banner.Title>
              </Banner.Body>
            </Banner>
          )}
          {logic.runs.data?.length === 0 && (
            <div className="rounded-lg border border-outline bg-surface-elevated py-12 text-center">
              <p className="text-ink-muted">You haven't run this report yet.</p>
              <p className="mt-1 text-xs text-ink-subtle">
                Your runs show here. Other people's runs stay private to them.
              </p>
            </div>
          )}
          {(logic.runs.data?.length ?? 0) > 0 && (
            <div className="overflow-x-auto rounded-lg border border-outline">
              <Table.Root className="w-full text-sm">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Started</Table.HeaderCell>
                    <Table.HeaderCell>Units</Table.HeaderCell>
                    <Table.HeaderCell>Period</Table.HeaderCell>
                    <Table.HeaderCell>Version</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell aria-label="Download" />
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(logic.runs.data ?? []).map((run) => (
                    <Table.Row key={run.id}>
                      <Table.Cell className="whitespace-nowrap text-ink-muted">
                        {formatDateTime(run.createdAt)}
                      </Table.Cell>
                      <Table.Cell>{runUnitsLabel(t.template.units, run.values)}</Table.Cell>
                      <Table.Cell className="text-ink-muted">
                        {periodLabel(run.values.period, run.createdAt)}
                      </Table.Cell>
                      <Table.Cell className="text-ink-muted">{run.templateVersion}</Table.Cell>
                      <Table.Cell>
                        <Badge variant={RUN_STATUS[run.status].variant}>
                          {RUN_STATUS[run.status].label}
                        </Badge>
                        {run.status === 'failed' && run.error && (
                          <p className="mt-1 max-w-md text-xs text-ink-muted">{run.error}</p>
                        )}
                      </Table.Cell>
                      <Table.Cell align="right">
                        {run.status === 'done' && (
                          <Button
                            variant="secondary"
                            size="sm"
                            className="gap-1.5"
                            aria-label={`Download the PDF of the run started ${formatDateTime(run.createdAt)}`}
                            onClick={() => void download(run.id)}
                          >
                            <Download size={15} aria-hidden />
                            Download
                          </Button>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </div>
          )}
        </section>
      )}
    </AppShell>
  );
};
